import type { ReactNode } from "react";
import { isApiClientError } from "@/shared/api/errors";
import { Banner } from "@/shared/ui/core/Banner";
import {
  DataTable,
  type DataTableColumn,
  type DataTableRowAction,
} from "@/shared/ui/core/DataTable";
import { EmptyState } from "@/shared/ui/core/EmptyState";
import { DetailSectionCard } from "@/shared/ui/layout/DetailSectionCard";

export type RelatedEntityTableProps<T> = {
  title: string;
  description?: string;
  /** Leading icon beside title (Hình 2 pattern). */
  icon?: ReactNode;
  /** Divider line separating title from table body (default: true). */
  headerDivider?: boolean;
  /** Custom right-hand summary or action in title row. */
  headerEnd?: ReactNode;
  /** Content inside the card, above the table. */
  beforeTable?: ReactNode;
  loading?: boolean;
  error?: unknown;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: ReactNode;
  rows: T[];
  getRowId: (row: T) => string;
  columns: DataTableColumn<T>[];
  onRowClick?: (row: T) => void;
  showRowActions?: boolean;
  showRowNumbers?: boolean;
  /** 1-based index of the first visible row when `rows` is a page slice. */
  rowNumberStart?: number;
  getRowActions?: (row: T) => DataTableRowAction[];
  minWidthClassName?: string;
  /** Shown as count summary next to title row when defined. */
  count?: number;
  /** Keep column headers when there are no rows. */
  emptyKeepHeader?: boolean;
  renderMobileCard?: (row: T) => ReactNode;
  /** Force mobile card view regardless of viewport width. */
  forceMobileView?: boolean;
  tableFooter?: ReactNode;
  mobileFooter?: ReactNode;
};

/**
 * Shared Lớp B related list for Ops detail tabs (R3.1).
 * Presentational only — caller owns fetch / domain mapping.
 * Uses DetailSectionCard with icon + divider (Hình 2) and subtle DataTable (Hình 1).
 */
export function RelatedEntityTable<T>({
  title,
  description,
  icon,
  headerDivider = true,
  headerEnd,
  beforeTable,
  loading = false,
  error,
  emptyTitle = "Chưa có dữ liệu",
  emptyDescription,
  emptyIcon,
  rows,
  getRowId,
  columns,
  onRowClick,
  showRowActions,
  showRowNumbers = true,
  rowNumberStart,
  getRowActions,
  minWidthClassName = "min-w-[480px]",
  count,
  emptyKeepHeader = false,
  renderMobileCard,
  forceMobileView,
  tableFooter,
  mobileFooter,
}: RelatedEntityTableProps<T>): ReactNode {
  const badgeCount = count ?? rows.length;
  const resolvedHeaderEnd =
    headerEnd !== undefined
      ? headerEnd
      : !loading && !error && badgeCount > 0
        ? (
            <span className="text-caption text-text-muted">
              {badgeCount} bản ghi
            </span>
          )
        : null;

  return (
    <DetailSectionCard
      icon={icon}
      title={title}
      description={description}
      headerDivider={headerDivider}
      headerEnd={resolvedHeaderEnd}
    >
      {beforeTable}
      {loading ? (
        <p className="m-0 text-caption text-text-secondary">Đang tải…</p>
      ) : error ? (
        <Banner
          tone="danger"
          title={
            isApiClientError(error)
              ? error.message
              : "Không tải được dữ liệu liên quan"
          }
        />
      ) : rows.length === 0 && !emptyKeepHeader ? (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={emptyIcon}
        />
      ) : (
        <DataTable<T>
          variant="subtle"
          showRowNumbers={showRowNumbers}
          rowNumberStart={rowNumberStart}
          getRowId={getRowId}
          rows={rows}
          columns={columns}
          onRowClick={onRowClick}
          showRowActions={showRowActions}
          getRowActions={getRowActions}
          minWidthClassName={minWidthClassName}
          renderMobileCard={renderMobileCard}
          forceMobileView={forceMobileView}
          emptyKeepHeader={emptyKeepHeader}
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
          emptyIcon={emptyIcon}
          tableFooter={tableFooter}
          mobileFooter={mobileFooter}
        />
      )}
    </DetailSectionCard>
  );
}
